// @ts-nocheck
import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  Icon,
  useColorModeValue,
  VStack,
  HStack,
  Tooltip,
  keyframes,
} from '@chakra-ui/react';
import { FiChevronDown, FiBell, FiAlertTriangle } from 'react-icons/fi';
import Card from 'components/card/Card';
import { PermissionWrapper } from 'components/shared/PermissionWrapper';
import { useQuery } from '@tanstack/react-query';
import {
  apiGetDashboardStatus,
  apiGetDashboardMetrics,
  apiGetSalesPerformance,
  apiGetViewAsOptions,
  buildViewAsQS,
} from 'api';
import type { ViewAsState } from 'api';
import Storage from 'storage';
import { SalesContestDashboard } from '../sales_contest_dashboard/SalesContestDashboard';
import { AuthContext } from 'contexts/AuthContext';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { PromoBanner } from './components/PromoBanner';
import { ImportantUpdatesFeed } from './components/ImportantUpdatesFeed';
import { QuickLinks } from './components/QuickLinks';
import { BookPremiumWidget } from './components/BookPremiumWidget';
import { PerformanceToPlanDonut } from './components/PerformanceToPlanDonut';
import { BrokerHealthScoreGauge } from './components/BrokerHealthScoreGauge';
import { BrokerSearchPanel } from './components/BrokerSearchPanel';

// ── Animations ───────────────────────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Gauge / Progress Bar — matches design: label left, large % right, flat bar ─
const GaugeComponent = ({
  value,
  label,
  disabled = false,
}: {
  value: number;
  label: string;
  disabled?: boolean;
}) => {
  const textColor = useColorModeValue('navy.700', 'white');
  const trackColor = useColorModeValue('#E8ECF4', '#2D3748');

  const barColor = disabled
    ? '#CBD5E0'
    : value < 40
      ? '#EF4444'
      : value <= 75
        ? '#EAB308'
        : '#10B981';

  return (
    <Box w="100%" opacity={disabled ? 0.45 : 1}>
      {/* Header row: bold label left, large % right (or "Coming Soon" badge when disabled) */}
      <Flex
        justify="space-between"
        align="baseline"
        mb={{ base: '2px', hxl: '4px' }}
      >
        <Text
          fontSize={{ base: 'xs', hxl: 'sm' }}
          fontWeight="700"
          color={textColor}
          lineHeight="1"
        >
          {label}
        </Text>
        {disabled ? (
          <Text
            fontSize={{ base: '8px', hxl: '9px' }}
            fontWeight="700"
            color="gray.300"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Coming Soon
          </Text>
        ) : (
          <Text
            fontSize={{ base: 'md', hxl: '2xl' }}
            fontWeight="800"
            color={textColor}
            lineHeight="1"
          >
            {value}%
          </Text>
        )}
      </Flex>

      {/* Flat progress bar */}
      <Box
        h={{ base: '8px', hxl: '14px' }}
        bg={trackColor}
        borderRadius="full"
        overflow="hidden"
        mb={{ base: '2px', hxl: '3px' }}
      >
        <Box
          h="100%"
          w={`${value}%`}
          bg={barColor}
          borderRadius="full"
          transition="width 0.8s ease-out"
        />
      </Box>

      {/* Scale labels */}
      <Flex justify="space-between" px="1px">
        {['0', '25', '50', '75', '100'].map((v) => (
          <Text
            key={v}
            fontSize={{ base: '8px', hxl: '10px' }}
            color="gray.400"
            fontWeight="500"
          >
            {v}
          </Text>
        ))}
      </Flex>
    </Box>
  );
};

// ── Dashboard Card — matches operations-dashboard DashboardCard design ────────
const StatusBox = ({
  count,
  label,
  isNewCount = 0,
  subtitleCount = 0,
  subtitleLabel = '',
  isComingSoon = false,
  isLoading = false,
  index = 0,
  onClick,
  viewOnlyTooltip,
}: {
  count: number | string;
  label: string;
  isNewCount?: number;
  subtitleCount?: number;
  subtitleLabel?: string;
  isComingSoon?: boolean;
  isLoading?: boolean;
  index?: number;
  onClick?: () => void;
  viewOnlyTooltip?: string;
}) => {
  const textColor = useColorModeValue('navy.700', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const comingSoonBg = useColorModeValue('gray.100', 'navy.900');
  const cardBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const footerBg = useColorModeValue('gray.50', 'navy.900');
  const footerBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const ringNormal = useColorModeValue('gray.200', 'whiteAlpha.200');

  const hasSubtitle = subtitleCount > 0 && !isComingSoon;
  const hasOverdueIcon = hasSubtitle;
  const hasNotification = isNewCount > 0 && !isComingSoon;
  const isReadOnly = !!viewOnlyTooltip;

  const card = (
    <Box
      bg={isComingSoon ? comingSoonBg : cardBg}
      borderRadius="xl"
      boxShadow="sm"
      border="1px solid"
      borderColor={cardBorder}
      display="flex"
      flexDirection="column"
      alignItems="center"
      pt="28px"
      pb="18px"
      px="2px"
      w="100%"
      cursor={isComingSoon || isReadOnly ? 'default' : 'pointer'}
      onClick={!isComingSoon && !isReadOnly ? onClick : undefined}
      animation={`${fadeInUp} 0.4s ease-out ${index * 0.07}s both`}
      transition="transform 0.15s ease, box-shadow 0.15s ease"
      opacity={isReadOnly ? 0.75 : 1}
      _hover={
        isComingSoon || isReadOnly
          ? {}
          : { transform: 'translateY(-2px)', boxShadow: 'md' }
      }
      userSelect="none"
    >
      {/* ── Circle ── */}
      <Box
        position="relative"
        w={{ base: '96px', hxl: '120px' }}
        h={{ base: '96px', hxl: '120px' }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb="16px"
        flexShrink={0}
      >
        {/* Ring — red gradient when any overdue counts, plain border otherwise */}
        {hasOverdueIcon ? (
          <>
            {/* Gradient outer circle */}
            <Box
              position="absolute"
              inset="0"
              borderRadius="full"
              bg="linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
              boxShadow="0 0 20px 4px rgba(239,68,68,0.18)"
              transition="all 0.3s"
            />
            {/* Inner mask — same bg as card, cuts out the center leaving only the gradient ring */}
            <Box
              position="absolute"
              inset="6px"
              borderRadius="full"
              bg={isComingSoon ? comingSoonBg : cardBg}
            />
          </>
        ) : (
          <Box
            position="absolute"
            inset="0"
            border="6px solid"
            borderRadius="full"
            borderColor={ringNormal}
            transition="all 0.3s"
          />
        )}

        <Flex
          direction="column"
          align="center"
          justify="center"
          zIndex={1}
          w={{ base: '78px', hxl: '96px' }}
        >
          {isLoading && !isComingSoon ? (
            <Box
              w="40px"
              h="22px"
              bg="gray.200"
              borderRadius="md"
              mb="4px"
              sx={{
                animation: 'pulse 1.4s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%,100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
          ) : (
            <Text
              w="100%"
              fontSize={{ base: 'xl', hxl: '2xl' }}
              fontWeight="800"
              color={textColor}
              lineHeight="1"
              letterSpacing="-0.02em"
              textAlign="center"
              mb="4px"
            >
              {isComingSoon ? '—' : String(count)}
            </Text>
          )}
          <Box
            w="32px"
            h="2.5px"
            bg="orange.500"
            mb="4px"
            borderRadius="full"
          />
          <Text
            w="100%"
            fontSize="10px"
            fontWeight="700"
            color={mutedColor}
            textTransform="uppercase"
            letterSpacing="0.07em"
            textAlign="center"
            lineHeight="1.35"
          >
            {label}
          </Text>
        </Flex>

        {(hasNotification || hasOverdueIcon) && (
          <Flex
            position="absolute"
            top={{ base: '16px', hxl: '22px' }}
            right="2px"
            gap="3px"
            zIndex={2}
          >
            {hasNotification && (
              <Tooltip
                label={`${isNewCount} New`}
                hasArrow
                placement="top"
                fontSize="xs"
              >
                <Box
                  p="4px"
                  borderRadius="full"
                  bg="orange.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 0 0 2px white"
                  cursor="default"
                >
                  <Icon as={FiBell} color="white" w="10px" h="10px" />
                </Box>
              </Tooltip>
            )}
            {hasOverdueIcon && (
              <Box
                p="4px"
                borderRadius="full"
                bg="red.600"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 0 0 2px white"
              >
                <Icon as={FiAlertTriangle} color="white" w="10px" h="10px" />
              </Box>
            )}
          </Flex>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box w="100%" mt="auto">
        {isComingSoon ? (
          <Flex
            bg={footerBg}
            borderRadius="lg"
            p="6px 8px"
            align="center"
            border="1px solid"
            borderColor={footerBorder}
            minH="32px"
            justify="center"
          >
            <Text
              fontSize="10px"
              fontWeight="800"
              color={mutedColor}
              textTransform="uppercase"
              letterSpacing="0.06em"
              textAlign="center"
              w="100%"
            >
              Coming Soon
            </Text>
          </Flex>
        ) : (
          <Flex gap="4px" w="100%">
            {/* New box — count on top, label below */}
            <Flex
              flex="1"
              direction="column"
              bg={footerBg}
              borderRadius="lg"
              p="6px 4px"
              align="center"
              justify="center"
              border="1px solid"
              borderColor={footerBorder}
              minH="44px"
            >
              <Text
                fontSize="md"
                fontWeight="900"
                color={isNewCount > 0 ? 'orange.500' : mutedColor}
                lineHeight="1"
              >
                {isNewCount}
              </Text>
              <Text
                fontSize="9px"
                fontWeight="800"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="0.05em"
                lineHeight="1.2"
                mt="3px"
              >
                New
              </Text>
            </Flex>
            {/* Overdue box — count on top, label below */}
            <Flex
              flex="1"
              direction="column"
              bg={footerBg}
              borderRadius="lg"
              p="6px 4px"
              align="center"
              justify="center"
              border="1px solid"
              borderColor={footerBorder}
              minH="44px"
            >
              <Text
                fontSize="md"
                fontWeight="900"
                color={subtitleCount > 0 ? 'red.600' : mutedColor}
                lineHeight="1"
              >
                {subtitleCount}
              </Text>
              <Text
                fontSize="9px"
                fontWeight="800"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="0.05em"
                lineHeight="1.2"
                mt="3px"
              >
                Overdue
              </Text>
            </Flex>
          </Flex>
        )}
      </Box>
    </Box>
  );

  if (isReadOnly && viewOnlyTooltip) {
    return (
      <Tooltip label={viewOnlyTooltip} placement="top" hasArrow fontSize="xs">
        {card}
      </Tooltip>
    );
  }

  return card;
};

// ── RedesignHome ──────────────────────────────────────────────────────────────
const RedesignHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const textColor = useColorModeValue('secondaryGray.900', 'white') as string;
  const bgColor = useColorModeValue('white', 'navy.700') as string;

  const isAnalyticsEnabled = useFeatureFlag('enable-analytics-dashboard');
  const isHomepageRedesign = useFeatureFlag('enable-homepage-redesign');
  const isViewAsEnabled = useFeatureFlag('enable-desktop-view-as');

  const [isDeskExpanded, setIsDeskExpanded] = useState(true);
  const [viewAs, setViewAs] = useState<ViewAsState>({ type: 'self' });
  const [isBrokerPanelOpen, setIsBrokerPanelOpen] = useState(false);
  const deskHeaderRef = useRef<HTMLDivElement>(null);

  // Dashboard status — useQuery for deduplication, caching, and isLoading state.
  // queryKey includes viewAs so switching view busts the cache immediately.
  const activeViewAs = isViewAsEnabled ? viewAs : undefined;
  const isViewingOther = !!(activeViewAs && activeViewAs.type !== 'self');

  // Reset view-as when the user navigates away from the homepage in the same tab.
  // Bucket clicks open in a new tab so this only fires on real page navigation.
  useEffect(() => {
    setViewAs({ type: 'self' });
  }, [location.pathname]);

  // Build query string for bucket deep-links — reuses the same function as API calls.
  const viewAsQS = buildViewAsQS(activeViewAs);

  const { data: rawStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['dashboard-status', activeViewAs],
    queryFn: () => apiGetDashboardStatus(activeViewAs),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const statusData = useMemo(() => {
    const n = (obj: any, field: string) =>
      typeof obj?.[field] === 'number' ? obj[field] : 0;
    const cnt = (obj: any) =>
      typeof obj?.count === 'number' ? obj.count : '-';
    const d = rawStatus as any;
    return {
      leads: {
        count: cnt(d?.leads),
        is_new_count: n(d?.leads, 'is_new_count'),
        unactioned_count: n(d?.leads, 'unactioned_count'),
      },
      work_in_progress: {
        count: cnt(d?.work_in_progress),
        is_new_count: n(d?.work_in_progress, 'is_new_count'),
        not_accessed_7d_count: n(d?.work_in_progress, 'not_accessed_7d_count'),
      },
      binders: {
        count: cnt(d?.binders),
        is_new_count: n(d?.binders, 'is_new_count'),
        unreleased_past_effective_count: n(
          d?.binders,
          'unreleased_past_effective_count',
        ),
      },
      tasks: {
        count: cnt(d?.tasks),
        is_new_count: n(d?.tasks, 'is_new_count'),
      },
      renewals: {
        count: cnt(d?.renewals),
        is_new_count: n(d?.renewals, 'is_new_count'),
      },
      cancellations: {
        count: cnt(d?.cancellations),
        is_new_count: n(d?.cancellations, 'is_new_count'),
      },
      document_score:
        typeof d?.document_score === 'number' ? d.document_score : 0,
      notifications: Array.isArray(d?.notifications)
        ? d.notifications
        : ([] as any[]),
    };
  }, [rawStatus]);

  const storedUser = Storage.get('user');
  const firstName = user?.first_name || storedUser?.first_name || '';
  const lastName = user?.last_name || storedUser?.last_name || '';
  const userName = firstName ? `${firstName} ${lastName}`.trim() : null;

  const { data: viewAsOptions } = useQuery<{
    branches: { id: number; name: string }[];
    is_admin: boolean;
  }>({
    queryKey: ['view-as-options'],
    queryFn: () => apiGetViewAsOptions() as any,
    staleTime: 5 * 60 * 1000,
    enabled: isViewAsEnabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const canViewAs = isViewAsEnabled && Boolean(viewAsOptions);
  const isAdmin = viewAsOptions?.is_admin === true;
  const isBranchOperator = canViewAs && !isAdmin;
  const firstBranch = viewAsOptions?.branches?.[0] ?? null;

  // Analytics KPIs — only fetched when enable-analytics-dashboard flag is on.
  const { data: metricsData } = useQuery({
    queryKey: ['dashboard-metrics', activeViewAs],
    queryFn: async () => {
      const res = await apiGetDashboardMetrics(activeViewAs);
      return res?.analytics ?? null;
    },
    enabled: isAnalyticsEnabled,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const analyticsMonthly = isAnalyticsEnabled
    ? (metricsData as { monthly?: Record<string, number> } | null)?.monthly
    : undefined;

  const healthScore: number = (analyticsMonthly?.score as number) ?? 0;

  // P2P percent + pace — sourced from sales-performance (same cache as SalesContestDashboard).
  // Shared queryKey ['sales-performance'] deduplicates with SalesContestDashboard's own query.
  // Gated on homepage-redesign FF (not analytics FF — P2P data comes from vw_BrokerP2P, not analytics views).
  const { data: salesData } = useQuery({
    queryKey: ['sales-performance', activeViewAs],
    queryFn: () => apiGetSalesPerformance(activeViewAs) as Promise<any>,
    enabled: isHomepageRedesign,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // P2P row selection — what the donut and pace show:
  //   branch view  → broker_performance (backend maps branch P2P here; no branch_list)
  //   broker view  → broker_performance (target broker's individual P2P)
  //   manager self → broker_performance (manager's own P2P)
  //   admin self   → broker_performance → big_performance fallback
  const p2pRow = (() => {
    if (!salesData) return null;
    if (viewAs.type === 'branch') {
      return (salesData as any).broker_performance ?? null;
    }
    if (viewAs.type === 'broker') {
      return (salesData as any).broker_performance ?? null;
    }
    return (
      (salesData as any).broker_performance ??
      (salesData as any).big_performance ??
      null
    );
  })();

  const p2pPercent: number = p2pRow?.percentage ?? 0;
  const p2pPace: number = p2pRow?.pace ?? 0;

  // Badges come from the status endpoint (user-specific, no FF gate)
  const recognitionBadges: {
    id: number;
    name: string;
    image_url: string | null;
  }[] = (rawStatus as any)?.recognition_badges ?? [];

  return (
    <Box position="relative">
      {/* Request Help — fixed tab on the right viewport edge */}
      <Tooltip label="Coming soon" hasArrow placement="left">
        <Box
          position="fixed"
          right="0"
          top="50%"
          transform="translateY(-50%)"
          bg="navy.700"
          _dark={{ bg: 'navy.600' }}
          color="white"
          px="10px"
          py="18px"
          borderRadius="8px 0 0 8px"
          cursor="default"
          zIndex={200}
          userSelect="none"
          boxShadow="-2px 2px 12px rgba(0,0,0,0.18)"
          sx={{ writingMode: 'vertical-lr', transform: 'translateY(-50%)' }}
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            color="white"
            letterSpacing="0.06em"
          >
            Request Help
          </Text>
        </Box>
      </Tooltip>

      <Box px="0" pt="0" pb="8px">
        <Grid templateColumns={{ base: '1fr', hxl: '1fr 375px' }} gap="8px">
          {/* ── Main content column ─────────────────────────────────────── */}
          <GridItem minW="0">
            <VStack spacing="8px" align="stretch">
              {/* 1. Promotional Carousel ───────────────────────────────── */}
              <PromoBanner />

              {/* 2. Broker's Desk ─────────────────────────────────────── */}
              <Box>
                {/* Header row */}
                <Flex
                  ref={deskHeaderRef}
                  align="center"
                  userSelect="none"
                  gap="10px"
                >
                  <Flex align="center" gap="6px">
                    {/* Title — opens broker selector for View-As operators when FF on */}
                    <Text
                      fontSize="lg"
                      color={textColor}
                      fontWeight="700"
                      cursor={canViewAs ? 'pointer' : 'default'}
                      _hover={canViewAs ? { opacity: 0.8 } : undefined}
                      transition="opacity 0.2s"
                      onClick={() => {
                        if (canViewAs) {
                          setIsBrokerPanelOpen((o) => !o);
                        }
                      }}
                    >
                      {viewAs.type === 'broker'
                        ? `${viewAs.name}'s Desktop`
                        : viewAs.type === 'branch'
                          ? `${viewAs.branchName} — Branch View`
                          : userName
                            ? `${userName}'s Desktop`
                            : "Broker's Desktop"}
                    </Text>
                    {/* Chevron — always collapses/expands the deck */}
                    <Icon
                      as={FiChevronDown}
                      w="18px"
                      h="18px"
                      color={textColor}
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                      transition="transform 0.25s ease, opacity 0.2s"
                      transform={
                        canViewAs
                          ? isBrokerPanelOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)'
                          : isDeskExpanded
                            ? 'rotate(0deg)'
                            : 'rotate(-90deg)'
                      }
                      onClick={() => setIsDeskExpanded((o) => !o)}
                    />
                  </Flex>

                  {/* Branch View pill — branch operators only, view-as FF must be on */}
                  {isBranchOperator && (
                    <Button
                      size="xs"
                      variant={viewAs.type === 'branch' ? 'solid' : 'outline'}
                      colorScheme={viewAs.type === 'branch' ? 'orange' : 'gray'}
                      borderRadius="20px"
                      fontWeight="600"
                      fontSize="xs"
                      px="12px"
                      isDisabled={!firstBranch}
                      onClick={() => {
                        setIsBrokerPanelOpen(false);
                        if (viewAs.type === 'branch') {
                          setViewAs({ type: 'self' });
                        } else if (firstBranch) {
                          setViewAs({
                            type: 'branch',
                            branchId: firstBranch.id,
                            branchName: firstBranch.name,
                          });
                        }
                      }}
                    >
                      Branch View
                    </Button>
                  )}

                  {/* Reset badge — shown when impersonating, FF must be on */}
                  {isViewAsEnabled && viewAs.type !== 'self' && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      borderRadius="20px"
                      fontSize="xs"
                      px="10px"
                      onClick={() => {
                        setViewAs({ type: 'self' });
                        setIsBrokerPanelOpen(false);
                      }}
                    >
                      ✕ Reset
                    </Button>
                  )}

                  {/* Legend */}
                  <Flex align="center" gap="8px" ml="auto">
                    <Text fontSize="xs" color="gray.400" fontWeight="600">
                      Legend:
                    </Text>
                    <HStack spacing="4px">
                      <Box
                        bg="orange.400"
                        borderRadius="full"
                        w="22px"
                        h="22px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="11px"
                        flexShrink={0}
                      >
                        🔔
                      </Box>
                      <Text fontSize="xs" color="gray.500">
                        New Activity
                      </Text>
                    </HStack>
                    <HStack spacing="4px">
                      <Box
                        bg="#DC2626"
                        borderRadius="full"
                        w="22px"
                        h="22px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="11px"
                        color="white"
                        flexShrink={0}
                        style={{ boxShadow: '0 0 7px rgba(220,38,38,0.75)' }}
                      >
                        ⚠
                      </Box>
                      <Text fontSize="xs" color="gray.500">
                        Overdue
                      </Text>
                    </HStack>
                  </Flex>
                </Flex>

                {/* Floating broker search panel — portal via position:fixed */}
                {isViewAsEnabled && isBrokerPanelOpen && canViewAs && (
                  <BrokerSearchPanel
                    viewAs={viewAs}
                    onChange={(s) => {
                      setViewAs(s);
                    }}
                    onClose={() => setIsBrokerPanelOpen(false)}
                    anchorRef={deskHeaderRef}
                    isAdmin={isAdmin}
                    isManager={isBranchOperator}
                    initialBranch={
                      viewAs.type === 'branch'
                        ? { id: viewAs.branchId, name: viewAs.branchName }
                        : viewAs.type === 'broker' && viewAs.branchId
                          ? { id: viewAs.branchId, name: viewAs.branchName! }
                          : null
                    }
                  />
                )}

                {/* Collapsible body — cards in a single horizontally-scrollable row */}
                <Box
                  maxH={isDeskExpanded ? '290px' : '0px'}
                  overflowY="hidden"
                  overflowX={isDeskExpanded ? 'auto' : 'hidden'}
                  transition="max-height 0.4s ease-in-out"
                  mt={isDeskExpanded ? '8px' : '0px'}
                  pb="4px"
                  sx={{
                    '&::-webkit-scrollbar': { height: '6px' },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.18)',
                      borderRadius: '3px',
                    },
                  }}
                >
                  <Grid
                    templateColumns={{
                      base: 'repeat(7, minmax(104px, 1fr))',
                      hxl: 'repeat(7, minmax(130px, 1fr))',
                    }}
                    gap="4px"
                    alignItems="stretch"
                    minW="100%"
                  >
                    <StatusBox
                      count={statusData.leads.count}
                      label="Leads"
                      isNewCount={statusData.leads.is_new_count}
                      subtitleCount={statusData.leads.unactioned_count ?? 0}
                      subtitleLabel="Overdue"
                      isLoading={isLoadingStatus}
                      index={0}
                      onClick={
                        isViewingOther
                          ? () =>
                              window.open(
                                `/admin/leads/list${viewAsQS}`,
                                '_blank',
                              )
                          : () => navigate('/admin/leads/list')
                      }
                    />
                    <StatusBox
                      count={statusData.work_in_progress.count}
                      label="WIP"
                      isNewCount={statusData.work_in_progress.is_new_count}
                      subtitleCount={
                        statusData.work_in_progress.not_accessed_7d_count ?? 0
                      }
                      subtitleLabel="Overdue"
                      isLoading={isLoadingStatus}
                      index={1}
                      onClick={
                        isViewingOther
                          ? () =>
                              window.open(
                                `/admin/policies/work-in-progress${viewAsQS}`,
                                '_blank',
                              )
                          : () => navigate('/admin/policies/work-in-progress')
                      }
                    />
                    <StatusBox
                      count={statusData.binders.count}
                      label="Binders"
                      isNewCount={statusData.binders.is_new_count}
                      subtitleCount={
                        statusData.binders.unreleased_past_effective_count ?? 0
                      }
                      subtitleLabel="Overdue"
                      isLoading={isLoadingStatus}
                      index={2}
                      onClick={
                        isViewingOther
                          ? () =>
                              window.open(
                                `/admin/policies/list${viewAsQS}`,
                                '_blank',
                              )
                          : () => navigate('/admin/policies/list')
                      }
                    />
                    <StatusBox
                      count={0}
                      label="Billing"
                      subtitleLabel="Overdue"
                      isComingSoon={true}
                      index={3}
                    />
                    <StatusBox
                      count={statusData.tasks.count}
                      label="Tasks"
                      isNewCount={statusData.tasks.is_new_count}
                      subtitleLabel="Overdue"
                      isComingSoon={true}
                      index={4}
                    />
                    <StatusBox
                      count={statusData.renewals.count}
                      label="Renewals"
                      isNewCount={statusData.renewals.is_new_count}
                      subtitleLabel="Overdue"
                      isComingSoon={true}
                      index={5}
                    />
                    <StatusBox
                      count={statusData.cancellations.count}
                      label="Cancellations"
                      isNewCount={statusData.cancellations.is_new_count}
                      subtitleLabel="Overdue"
                      isComingSoon={true}
                      index={6}
                    />
                  </Grid>
                </Box>
              </Box>

              {/* 3. KPI bar — orange gradient band ──────────────────────── */}
              {/* Font-size drives all em units below: clamp scales continuously
                  with viewport width just like the banner's width:100%/height:auto. */}
              <PermissionWrapper cardId="homepage-kpi-bar">
                <Box
                  sx={{ fontSize: 'clamp(9px, 1.1vw, 14px)' }}
                  bg="linear-gradient(to right, #f97316, #fb923c)"
                  borderRadius="1.14em"
                  px="1em"
                  py="0.57em"
                  w="100%"
                >
                  <Flex
                    align="center"
                    justify="space-between"
                    gap="0.57em"
                    flexWrap="wrap"
                  >
                    {/* Recognition badges — shown only when the member has at least one */}
                    {recognitionBadges.length > 0 && (
                      <HStack spacing="0.71em" flexWrap="wrap" ms="auto">
                        {recognitionBadges.map((b) => (
                          <Box
                            key={b.id}
                            px="0.71em"
                            py="0.43em"
                            borderRadius="1.14em"
                            bg={
                              b.image_url
                                ? 'navy.800'
                                : 'linear-gradient(135deg, #C53030 0%, #9B2335 100%)'
                            }
                            display="flex"
                            alignItems="center"
                            gap={b.image_url ? '0.71em' : '0'}
                            minH="3.71em"
                            overflow="hidden"
                            boxShadow="0 2px 12px rgba(0,0,0,0.25)"
                            position="relative"
                          >
                            {b.image_url && (
                              <Box
                                flexShrink={0}
                                w="2.86em"
                                h="2.86em"
                                overflow="hidden"
                              >
                                <img
                                  src={b.image_url}
                                  alt={b.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            )}
                            <Text
                              fontSize="1.14em"
                              color="white"
                              fontWeight="700"
                              letterSpacing="0.01em"
                              whiteSpace="nowrap"
                            >
                              {b.name}
                            </Text>
                          </Box>
                        ))}
                      </HStack>
                    )}
                  </Flex>
                </Box>
              </PermissionWrapper>

              {/* 4. BIG Sales Contest + Book Size / Premium Written ───── */}
              <PermissionWrapper cardId="sales-dashboard">
                <Flex
                  gap="8px"
                  align="stretch"
                  direction={{ base: 'column', hxl: 'row' }}
                >
                  {/* ~75% */}
                  <Box flex="3" minW="0">
                    <SalesContestDashboard viewAs={activeViewAs} />
                  </Box>
                  {/* ~25% — grayed out when analytics disabled */}
                  <Tooltip
                    label={!isAnalyticsEnabled ? 'Coming Soon' : ''}
                    hasArrow
                    placement="top"
                    isDisabled={isAnalyticsEnabled}
                  >
                    <Box
                      flex="1"
                      w="100%"
                      minW={{ base: '0', hxl: '200px' }}
                      maxW={{ base: '100%', hxl: '340px' }}
                      display="flex"
                      flexDirection="column"
                      opacity={isAnalyticsEnabled ? 1 : 0.45}
                      filter={isAnalyticsEnabled ? 'none' : 'grayscale(1)'}
                      pointerEvents={isAnalyticsEnabled ? 'auto' : 'none'}
                      cursor={isAnalyticsEnabled ? 'auto' : 'default'}
                      userSelect={isAnalyticsEnabled ? 'auto' : 'none'}
                    >
                      <BookPremiumWidget
                        analytics={
                          isAnalyticsEnabled ? (metricsData as any) : {}
                        }
                        analyticsEnabled={isAnalyticsEnabled}
                      />
                    </Box>
                  </Tooltip>
                </Flex>
              </PermissionWrapper>

              {/* 5. Metrics ───────────────────────────────────────────── */}
              <PermissionWrapper cardId="homepage-metrics">
                <Card bg={bgColor} p="10px" borderRadius="16px">
                  <Flex align="stretch" direction="row">
                    {/* ── Left 60%: Health Score + Bars — gated by analytics FF ── */}
                    <Tooltip
                      label={!isAnalyticsEnabled ? 'Coming Soon' : ''}
                      hasArrow
                      placement="top"
                      isDisabled={isAnalyticsEnabled}
                    >
                      <Box
                        flex="3"
                        minW="0"
                        opacity={isAnalyticsEnabled ? 1 : 0.45}
                        filter={isAnalyticsEnabled ? 'none' : 'grayscale(1)'}
                        pointerEvents={isAnalyticsEnabled ? 'auto' : 'none'}
                        cursor={isAnalyticsEnabled ? 'auto' : 'default'}
                        userSelect={isAnalyticsEnabled ? 'auto' : 'none'}
                      >
                        <Text
                          fontSize="18px"
                          fontWeight="700"
                          color={textColor}
                          mb="4px"
                          px="4px"
                        >
                          Broker Health Score
                        </Text>
                        <Flex
                          align="center"
                          justify="center"
                          gap="8px"
                          w="100%"
                          flex="1"
                          direction="row"
                        >
                          <Box
                            flexShrink={0}
                            w={{ base: '160px', hxl: '200px' }}
                          >
                            <BrokerHealthScoreGauge score={healthScore} />
                          </Box>
                          <Box flex="1" minW="200px" pl="4px" pr="4px">
                            <VStack spacing="4px" align="stretch">
                              <GaugeComponent
                                value={Math.round(healthScore)}
                                label="Sales"
                              />
                              <GaugeComponent
                                value={0}
                                label="Service"
                                disabled={true}
                              />
                              <GaugeComponent
                                value={0}
                                label="Book of Business"
                                disabled={true}
                              />
                            </VStack>
                          </Box>
                        </Flex>
                      </Box>
                    </Tooltip>

                    {/* Separator — bars | P2P donut */}
                    <Box
                      flexShrink={0}
                      w="2px"
                      alignSelf="stretch"
                      bg="gray.300"
                      _dark={{ bg: 'whiteAlpha.200' }}
                      mx={{ base: '8px', hxl: '12px' }}
                    />

                    {/* ── Right 40%: Performance to Plan — gated by homepage-redesign FF ── */}
                    <Tooltip
                      label={!isHomepageRedesign ? 'Coming Soon' : ''}
                      hasArrow
                      placement="top"
                      isDisabled={isHomepageRedesign}
                    >
                      <Box
                        flex="2"
                        minW="0"
                        px="4px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        opacity={isHomepageRedesign ? 1 : 0.45}
                        filter={isHomepageRedesign ? 'none' : 'grayscale(1)'}
                        pointerEvents={isHomepageRedesign ? 'auto' : 'none'}
                        cursor={isHomepageRedesign ? 'auto' : 'default'}
                        userSelect={isHomepageRedesign ? 'auto' : 'none'}
                      >
                        <Text
                          fontSize="18px"
                          fontWeight="700"
                          color={textColor}
                          mb="4px"
                        >
                          Performance to Plan
                        </Text>
                        <PerformanceToPlanDonut
                          p2pPercent={p2pPercent}
                          paceToPlan={p2pPace}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </Card>
              </PermissionWrapper>
            </VStack>
          </GridItem>

          {/* ── Right sidebar ──
              When the sidebar is below the main column (narrow viewport), render
              Updates and Quick Links side-by-side so the page doesn't grow tall.
              When in the right column (lg+), keep them stacked vertically. */}
          <GridItem minW="0">
            <Flex
              direction={{ base: 'row', hxl: 'column' }}
              gap="8px"
              align="stretch"
            >
              {/* Recent Updates — wider (matches Broker Health Score ~60%) */}
              <Box flex="3" minW="0">
                <ImportantUpdatesFeed />
              </Box>
              {/* Quick Links — narrower (matches Performance to Plan ~40%) */}
              <Box flex="2" minW="0">
                <QuickLinks />
              </Box>
            </Flex>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};

export default RedesignHome;
